import type {
  PlatformChatParticipant,
  PlatformChatSession,
  PlatformChatSessionListItemDTO,
  PlatformMessage,
  PlatformTurn,
} from '@liks/backend-svc-api/esm/models/index';
import type { PlatformChatSessionListItemWithIdentity } from '../types/chatSession';

export function hasId<T extends { id?: string | null }>(
  value: T,
): value is T & { id: string } {
  return typeof value.id === 'string' && value.id.length > 0;
}

export function isRecord(value: unknown): value is Record<string, unknown> {
  return value != null && typeof value === 'object';
}

export function isPlatformMessageValue(
  value: unknown,
): value is PlatformMessage {
  if (!isRecord(value)) {
    return false;
  }

  return value.role === 'USER' || value.role === 'ASSISTANT';
}

export function isPlatformTurnValue(value: unknown): value is PlatformTurn {
  if (!isRecord(value)) {
    return false;
  }

  return 'userMessage' in value || 'assistantMessage' in value;
}

export function hasUserId(
  participant: PlatformChatParticipant,
): participant is PlatformChatParticipant & { userId: string } {
  return (
    typeof participant.userId === 'string' && participant.userId.length > 0
  );
}

export function isPlatformChatSessionListItemWithIdentity(
  item: PlatformChatSessionListItemDTO,
): item is PlatformChatSessionListItemDTO & {
  session: PlatformChatSession & { id: string };
} {
  return item.session != null && hasId(item.session);
}

export function toPlatformChatSessionListItemWithIdentity(
  item: PlatformChatSessionListItemDTO & {
    session: PlatformChatSession & { id: string };
  },
): PlatformChatSessionListItemWithIdentity {
  return {
    session: item.session,
    pinned: item.pinned === true,
    participantCount:
      typeof item.participantCount === 'number' && item.participantCount >= 0
        ? item.participantCount
        : 1,
    mostRecentMessage: item.mostRecentMessage ?? null,
  };
}
